import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Heading } from '@astryxdesign/core/Heading';
import { Item } from '@astryxdesign/core/Item';
import { List } from '@astryxdesign/core/List';
import { Lightbox } from '@astryxdesign/core/Lightbox';
import { HStack, VStack } from '@astryxdesign/core/Stack';
import { Grid } from '@astryxdesign/core/Grid';
import { StatusDot } from '@astryxdesign/core/StatusDot';
import { Text } from '@astryxdesign/core/Text';
import { Token } from '@astryxdesign/core/Token';
import {
  ArrowRight, CalendarPlus, Camera, ChatCircleDots, Clock, Coffee, MapPin, NavigationArrow, Phone, ShieldCheck, Sparkle,
  Wrench, type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { useBookingNav, useStudio } from '../app/studio';
import { Glass } from '../components/Glass';
import { Reveal } from '../components/Reveal';
import { InstallCard } from '../components/InstallCard';
import { PreviewBanner } from '../components/PreviewBanner';
import { mediaUrl } from '../lib/api';
import { openStatus } from '../lib/hours';
import { durationLabel, hoursSummary, money } from '../lib/time';

const CARD_ICONS: Record<string, PhosphorIcon> = { ShieldCheck, Camera, Coffee, Wrench, Clock, Sparkle, MapPin };

export function HomePage() {
  const studio = useStudio();
  const booking = useBookingNav();
  const navigate = useNavigate();
  const [lightbox, setLightbox] = useState<number | null>(null);
  const status = useMemo(() => openStatus(studio), [studio]);
  const top = studio.services.slice(0, 5);
  const base = `/s/${studio.slug}`;
  const examples = studio.assistant_examples.length
    ? studio.assistant_examples
    : ['Когда ближайшее окно?', 'Сколько стоит услуга?', 'Как найти студию?'];

  return (
    <main>
      {/* ------------------------------------------------------------ hero */}
      <header className="hero" aria-label={studio.name}>
        <div className="hero__media">
          <img
            src={mediaUrl(studio.hero_path)}
            alt={`${studio.name}: главное фото`}
            fetchPriority="high"
            decoding="async"
          />
        </div>
        <div className="hero__shade" aria-hidden />
        <div className="hero__top">
          <HStack gap={3} vAlign="center" hAlign="between">
            <HStack gap={3} vAlign="center">
              {studio.logo_path && <img className="hero__logo" src={mediaUrl(studio.logo_path)} alt="" />}
              <Text weight="semibold">{studio.short_name}</Text>
            </HStack>
            {studio.status !== 'live' && <Token label="Образец" size="sm" color="yellow" />}
          </HStack>
        </div>
        <div className="hero__body">
          <VStack gap={3}>
            <h1 className="display-title">{studio.name}</h1>
            {studio.tagline && (
              <Text type="large" color="secondary" textWrap="pretty">
                {studio.tagline}
              </Text>
            )}
            <Glass as="button" type="button" className="hero-cta" snapshot=".hero__media" zIndex={5} onClick={() => booking.open()}>
              <span className="hero-cta__dot" aria-hidden />
              Записаться
            </Glass>
          </VStack>
        </div>
      </header>

      <div className="app-page">
        <VStack gap={8}>
          {studio.status !== 'live' && <PreviewBanner />}

          {/* -------------------------------------------------- quick facts */}
          <Reveal>
            <VStack gap={3} paddingInline={5} paddingBlockStart={2}>
              <HStack gap={2} vAlign="center">
                <StatusDot variant={status.open ? 'success' : 'neutral'} label={status.open ? 'открыто' : 'закрыто'} isPulsing={status.open} />
                <Text weight="medium">{status.text}</Text>
              </HStack>
              {studio.description && (
                <Text color="secondary" textWrap="pretty">
                  {studio.description}
                </Text>
              )}
            </VStack>
          </Reveal>

          {/* ------------------------------------------------ booking card */}
          <Reveal id="book">
            <VStack gap={3} paddingInline={4}>
              <Heading level={2}>Запись в студию</Heading>
              <Card padding={5}>
                <VStack gap={4}>
                  <Text color="secondary">
                    Выберите услугу и свободное время — без звонков и регистрации. Подтверждение придёт сразу.
                  </Text>
                  <div className="chip-row">
                    {studio.services.slice(0, 3).map((s) => (
                      <button key={s.id} type="button" className="chip" onClick={() => booking.open(s.id)}>
                        {s.name}
                      </button>
                    ))}
                  </div>
                  <Button
                    label="Выбрать услугу и время"
                    variant="primary"
                    size="lg"
                    width="100%"
                    icon={<CalendarPlus size={20} aria-hidden />}
                    onClick={() => booking.open()}
                  />
                </VStack>
              </Card>
            </VStack>
          </Reveal>

          {/* ---------------------------------------------------- services */}
          <Reveal>
            <VStack gap={2}>
              <HStack paddingInline={5} hAlign="between" vAlign="center">
                <Heading level={2}>Услуги и цены</Heading>
                <Button label="Все" variant="ghost" href={`${base}/services`} as={RouterLink} />
              </HStack>
              <VStack paddingInline={2}>
              <List hasDividers density="spacious">
                {top.map((s) => (
                  <Item
                    key={s.id}
                    label={<Text weight="semibold">{s.name}</Text>}
                    description={durationLabel(s.duration_min)}
                    endContent={
                      <HStack gap={2} vAlign="center">
                        <Text weight="semibold" hasTabularNumbers>
                          {money(s.price, studio.currency, s.price_from)}
                        </Text>
                        <ArrowRight size={18} aria-hidden />
                      </HStack>
                    }
                    onClick={() => booking.open(s.id)}
                  />
                ))}
              </List>
              </VStack>
            </VStack>
          </Reveal>

          {/* ------------------------------------------------------ gallery */}
          {studio.gallery.length > 0 && (
            <Reveal>
              <VStack gap={3}>
                <HStack paddingInline={5}>
                  <Heading level={2}>Наши работы</Heading>
                </HStack>
                <div className="h-scroll" role="list">
                  {studio.gallery.map((g, i) => (
                    <button key={g.id} type="button" className="work-card" role="listitem" onClick={() => setLightbox(i)}>
                      <VStack gap={2}>
                        <img src={mediaUrl(g.image_path)} alt={g.caption ?? 'Пример работы'} loading="lazy" decoding="async" />
                        {g.caption && (
                          <Text type="supporting" maxLines={2}>
                            {g.caption}
                          </Text>
                        )}
                      </VStack>
                    </button>
                  ))}
                </div>
                <Lightbox
                  isOpen={lightbox !== null}
                  onOpenChange={(o) => !o && setLightbox(null)}
                  index={lightbox ?? 0}
                  onIndexChange={setLightbox}
                  media={studio.gallery.map((g) => ({ src: mediaUrl(g.image_path)!, alt: g.caption ?? 'Пример работы', caption: g.caption ?? undefined }))}
                  hasZoom
                />
              </VStack>
            </Reveal>
          )}

          {/* --------------------------------------------------- info cards */}
          {studio.info_cards.length > 0 && (
            <Reveal>
              <Grid columns={{ minWidth: 220 }} gap={3} >
                {studio.info_cards.map((c, i) => {
                  const Ico = CARD_ICONS[c.icon ?? ''] ?? Sparkle;
                  return (
                    <VStack key={i} gap={2} paddingInline={5}>
                      <Ico size={28} weight="duotone" color="var(--color-accent)" aria-hidden />
                      <Text weight="semibold" type="large">
                        {c.title}
                      </Text>
                      <Text color="secondary">{c.text}</Text>
                    </VStack>
                  );
                })}
              </Grid>
            </Reveal>
          )}

          {/* ---------------------------------------------------- assistant */}
          <Reveal>
            <VStack gap={3} paddingInline={4}>
              <HStack gap={2} vAlign="center" paddingInline={1}>
                <ChatCircleDots size={24} color="var(--color-accent)" aria-hidden />
                <Heading level={2}>Спросите помощника</Heading>
              </HStack>
              <Text color="secondary">Отвечает по услугам, ценам и свободному времени этой студии.</Text>
              <div className="chip-row">
                {examples.map((q) => (
                  <button key={q} type="button" className="chip" onClick={() => navigate(`${base}/assistant?q=${encodeURIComponent(q)}`)}>
                    {q}
                  </button>
                ))}
              </div>
            </VStack>
          </Reveal>

          {/* ------------------------------------------------------ contacts */}
          <Reveal>
            <VStack gap={4} paddingInline={5}>
              <Heading level={2}>Как нас найти</Heading>
              <VStack gap={1}>
                <HStack gap={2} vAlign="start">
                  <MapPin size={22} aria-hidden />
                  <Text weight="medium">{studio.address}</Text>
                </HStack>
                {studio.address_hint && (
                  <Text color="secondary" textWrap="pretty">
                    {studio.address_hint}
                  </Text>
                )}
              </VStack>
              <VStack gap={1}>
                {hoursSummary(studio.hours).map((g) => (
                  <HStack key={g.days} hAlign="between">
                    <Text color="secondary">{g.days}</Text>
                    <Text hasTabularNumbers>{g.text}</Text>
                  </HStack>
                ))}
              </VStack>
              <HStack gap={2} wrap="wrap">
                {studio.map_url && (
                  <Button label="Маршрут" variant="secondary" icon={<NavigationArrow size={18} aria-hidden />} href={studio.map_url} target="_blank" rel="noopener" />
                )}
                {studio.phone && (
                  <Button label={studio.phone} variant="secondary" icon={<Phone size={18} aria-hidden />} href={`tel:${studio.phone.replace(/[^\d+]/g, '')}`} />
                )}
              </HStack>
            </VStack>
          </Reveal>

          <Reveal>
            <InstallCard />
          </Reveal>

          <HStack paddingInline={5} hAlign="center">
            <Button label="Кабинет владельца" variant="ghost" size="sm" href={`${base}/owner/`} as={RouterLink} />
          </HStack>
        </VStack>
      </div>
    </main>
  );
}
